import React, { Suspense, useEffect, useRef, useState } from 'react';

import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';

import { OFFLINE_GC_TIME_MS } from '@splitfiesta/core';
import { I18nProvider } from '@splitfiesta/i18n';
import {
  RefreshTokenInvalidError,
  isTokenExpired,
  refreshTokensWithEndpoint,
} from '@splitfiesta/platform-web/auth/cognito';

import { ExampleGroupSession } from '@/components/ExampleGroupSession';
import { ApplyUserLanguage } from '@/components/auth/ApplyUserLanguage';
import { AsyncErrorBoundary } from '@/components/common/AsyncErrorBoundary';
import { NativeAppPrompt } from '@/components/common/NativeAppPrompt';
import { OfflineIndicator } from '@/components/common/OfflineIndicator';
import { PWAUpdatePrompt } from '@/components/common/PWAUpdatePrompt';
import { SplitFiestaLoading } from '@/components/common/SplitFiestaLoading';
import { SkeletonDebugLayout } from '@/components/layout/SkeletonDebugLayout';

import { useOfflineSync } from '@/hooks/useOfflineSync';
import { useRouteChangeHandler } from '@/hooks/useRouteChangeHandler';
import { SkeletonDebugProvider } from '@/hooks/useSkeletonDebug';

import { APP_VERSION } from '@/lib/appVersion';
import { persistDehydrateOptions } from '@/lib/persistDehydrateOptions';
import { queryClient, queryPersister } from '@/lib/queryClient';

import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';

// Theme initializer component
function ThemeInitializer() {
  const theme = useUIStore(state => state.theme);
  const applyTheme = useUIStore(state => state.applyTheme);

  useEffect(() => {
    // Initialize theme on mount
    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)')
        .matches
        ? 'dark'
        : 'light';
      applyTheme(systemTheme);
    } else {
      applyTheme(theme as 'light' | 'dark');
    }

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      if (theme === 'system') {
        applyTheme(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, applyTheme]);

  return null;
}

// Auth initializer component
function AuthInitializer() {
  const initialize = useAuthStore(state => state.initialize);

  useEffect(() => {
    // Initialize auth state (Zustand persist middleware handles loading from localStorage)
    initialize();
  }, [initialize]);

  return null;
}

// Session validator component - validates tokens on mount
// Only clears auth when Cognito explicitly rejects the token (invalid_grant)
// Does NOT clear auth on network errors to support offline users
// Also syncs user profile from backend to correct any stale/wrong user data
function SessionValidator() {
  const idToken = useAuthStore(state => state.idToken);
  const refreshToken = useAuthStore(state => state.refreshToken);
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const hasHydrated = useAuthStore(state => state.hasHydrated);
  const setIdToken = useAuthStore(state => state.setIdToken);
  const setAuthFromDto = useAuthStore(state => state.setAuthFromDto);
  const clearAuth = useAuthStore(state => state.clearAuth);

  // Prevent double execution in StrictMode
  const hasValidated = useRef(false);

  useEffect(() => {
    // Wait for Zustand to hydrate from localStorage before checking tokens
    if (!hasHydrated) return;

    // Reset validation flag when user logs out so next login gets validated
    if (!isAuthenticated) {
      hasValidated.current = false;
      return;
    }

    if (!idToken) return;
    if (hasValidated.current) return;

    hasValidated.current = true;

    const validateSession = async () => {
      let activeToken = idToken;

      // Check if token is expired
      if (isTokenExpired(idToken)) {
        // Token is expired, try to refresh if we have a refresh token
        if (!refreshToken) {
          // No refresh token available - force re-login to get one
          // This is a one-time migration for users who logged in before
          // we started storing refresh tokens
          clearAuth();
          return;
        }

        try {
          // Try to refresh using the direct token endpoint
          const tokens = await refreshTokensWithEndpoint(refreshToken);
          // Update the ID token in the store
          setIdToken(tokens.idToken);
          activeToken = tokens.idToken;
        } catch (error) {
          // Only clear auth if Cognito explicitly rejected the token
          // Network errors should NOT clear auth (supports offline users)
          if (error instanceof RefreshTokenInvalidError) {
            clearAuth();
          }
          // For network errors, do nothing - user can continue offline
          return;
        }
      }

      // Sync user profile from backend to correct any stale/wrong user data
      // The backend resolves the correct userId from the JWT, so this ensures
      // the frontend always has the right user.id
      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
        const response = await fetch(`${apiUrl}/api/v1/auth/me`, {
          headers: { Authorization: `Bearer ${activeToken}` },
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setAuthFromDto(data.data, activeToken, refreshToken ?? undefined);
          }
        }
      } catch {
        // Network error - skip sync, user can continue with cached data
      }
    };

    validateSession();
  }, [
    idToken,
    refreshToken,
    isAuthenticated,
    hasHydrated,
    setIdToken,
    setAuthFromDto,
    clearAuth,
  ]);

  return null;
}

// Route change handler component
function RouteChangeHandler() {
  useRouteChangeHandler();
  return null;
}

// Offline sync initializer component
function OfflineSyncInitializer() {
  // Initialize offline sync - auto-syncs when coming back online
  useOfflineSync();
  return null;
}

// Persist options for React Query cache
// Note: queryPersister only saves when online to prevent overwriting good cache with failed queries
const persistOptions = {
  persister: queryPersister,
  maxAge: OFFLINE_GC_TIME_MS, // matches gcTime: an older entry would be restored only to be collected
  // A new deploy drops the previous build's snapshot on its first load.
  buster: APP_VERSION,
  dehydrateOptions: persistDehydrateOptions,
};

// Main Providers Component - Simplified with 2-strategy approach
export function Providers({ children }: { children: React.ReactNode }) {
  const [isRestored, setIsRestored] = useState(false);

  return (
    <AsyncErrorBoundary>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={persistOptions}
        onSuccess={() => setIsRestored(true)}
      >
        {/* Wait for cache to restore before rendering app - critical for offline support */}
        {!isRestored ? (
          <SplitFiestaLoading />
        ) : (
          <I18nProvider>
            <AuthInitializer />
            <SessionValidator />
            <ThemeInitializer />
            <RouteChangeHandler />
            <ApplyUserLanguage />
            <ExampleGroupSession />
            <OfflineSyncInitializer />
            <OfflineIndicator />
            <PWAUpdatePrompt />
            <NativeAppPrompt />
            {/* Wrap in Suspense for useSearchParams */}
            <Suspense fallback={<SplitFiestaLoading />}>
              <SkeletonDebugProvider>
                <SkeletonDebugLayout>{children}</SkeletonDebugLayout>
              </SkeletonDebugProvider>
            </Suspense>
          </I18nProvider>
        )}
        {import.meta.env.DEV && (
          <ReactQueryDevtools
            initialIsOpen={false}
            buttonPosition="bottom-left"
          />
        )}
      </PersistQueryClientProvider>
    </AsyncErrorBoundary>
  );
}
